import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigation, NavigationItem } from '@americanexpress/dls-react';
import { getIcon } from './Icons';

const Nav = (props) => {
  const {
    cssProperties,
    setFormSubmissionPayload,
    formSubmissionPayload,
    showInContainer = false,
    childElements,
    defaultOpenIds = [],
  } = props;

  const navItemRefs = useRef({});
  const lastVisitedRef = useRef(formSubmissionPayload.navDefaultSection ?? null);

  useEffect(() => {
    const incoming = formSubmissionPayload.navDefaultSection;
    if (incoming != null && (lastVisitedRef.current == null || incoming > lastVisitedRef.current)) {
      lastVisitedRef.current = incoming;
    }
  }, [formSubmissionPayload.navDefaultSection]);

  const handleNavClick = (navVal) => {
    if (navVal <= lastVisitedRef.current) {
      lastVisitedRef.current = Math.max(lastVisitedRef.current, navVal);
      setFormSubmissionPayload((prev) => ({
        ...prev,
        navDefaultSection: navVal,
        elemId: null,
      }));
    }
  };

  useEffect(() => {
    let frameId;
    const outerFrameId = requestAnimationFrame(() => {
      frameId = requestAnimationFrame(() => {
        defaultOpenIds.forEach((nodeId) => {
          const el = navItemRefs.current[nodeId];
          if (el) {
            const toggleTarget = el.querySelector('button') || el.firstElementChild;
            if (toggleTarget) toggleTarget.click();
          }
        });
      });
    });
    return () => {
      cancelAnimationFrame(outerFrameId);
      cancelAnimationFrame(frameId);
    };
  }, [defaultOpenIds]);

  const renderNavItems = (items, isRoot = false) => {
    if (!items || items.length === 0) return null;

    const nodes = items.map((item) => {
      const navId = `${item.pageNo}_${item.label}`;
      const nestedItems = item.childElements?.length > 0
        ? item.childElements
        : null;

      return (
        <div
          key={navId}
          ref={(el) => { if (item.nodeId) navItemRefs.current[item.nodeId] = el; }}
        >
          <NavigationItem
            id={navId}
            label={item.label}
            icon={item.icon ? getIcon(item.icon) : null}
            onClick={() => handleNavClick(item.value)}
            theme={{
              // only compare to 1 decimal to allow minor version differences
              color: (
                Number(item.value).toFixed(1)
                  === Number(formSubmissionPayload.navDefaultSection).toFixed(1)
              ) || isRoot ? '#00175a !important' : '#006fcf',
              ':hover': Number(item.value) > Number(lastVisitedRef.current)
                ? { cursor: 'not-allowed' }
                : { cursor: 'pointer', color: '#00175a !important' },
              fontWeight: isRoot ? 'bold' : 'normal',
            }}
          >
            {nestedItems ? renderNavItems(nestedItems, false) : null}
          </NavigationItem>
        </div>
      );
    });

    return nodes.length > 1 ? nodes : nodes[0];
  };

  return (
    <div className={cssProperties?.style}>
      <Navigation showInContainer={showInContainer}>
        {renderNavItems(childElements, true)}
      </Navigation>
    </div>
  );
};

Nav.propTypes = {
  cssProperties: PropTypes.objectOf(PropTypes.string),
  childElements: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      pageNo: PropTypes.number,
      childElements: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  showInContainer: PropTypes.bool,
  defaultOpenIds: PropTypes.arrayOf(PropTypes.string),
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  setFormSubmissionPayload: PropTypes.func,
};

export default Nav;
